import get from "lodash/get";
import pick from "lodash/pick";
import moment from "moment";
import { COACHING_FREE_TRIAL_DAYS } from "../constants";
import i18n from "../i18n";
import { canViewAllOrganizationClients } from "./organization-permissions-helpers";

export const isCoach = (user) => get(user, "permissions", []).includes("coach");

export const isCoachFreeTrial = (user) => user.coachingFreeTrial;

export const isCoachConverted = (user) =>
  user.coachingBilling?.plan?.active && user.coachingBilling.plan.usage_type;

export const getCoachStripePlan = (coach) => {
  if (coach.coachingBilling?.plan?.active) {
    switch (coach.coachingBilling.plan.usage_type) {
      case "metered":
        return i18n.t("common:userStatus.metered");
      case "licensed":
        return i18n.t("common:userStatus.enterprise");
      default:
        return i18n.t("common:userStatus.unknown");
    }
  }
  return i18n.t("common:userStatus.notAvailable");
};

export const getCoachTrialStatus = (user) => {
  if (isCoachFreeTrial(user)) {
    if (calculateFreeTrialDaysLeft(user) > 0) {
      return i18n.t("common:userStatus.freeTrial");
    }

    return i18n.t("common:userStatus.expiredFreeTrial");
  }
  if (isCoachConverted(user)) {
    return i18n.t("common:userStatus.converted");
  }

  if (user.coachingBilling) {
    return i18n.t("common:userStatus.cancelled");
  }

  return i18n.t("common:userStatus.noInformation");
};

export const isPushActiveCoach = (user) =>
  !(
    user.permissions?.includes("coach") &&
    user.pushAccountInfo &&
    (user.coachingBilling?.requestPaymentInfoOnNextLogin ||
      (user.coachingFreeTrial && calculateFreeTrialDaysLeft(user) < 1) ||
      ["unpaid", "canceled"].includes(user.coachingBilling?.status))
  );

export const calculateFreeTrialDaysLeft = (user) => {
  let daysLeft = 0;
  const coachingFreeTrial = user.coachingFreeTrial;
  if (isCoach(user) && coachingFreeTrial) {
    daysLeft =
      COACHING_FREE_TRIAL_DAYS -
      moment().diff(moment(coachingFreeTrial.startedAt), "days");
  }

  return daysLeft;
};

export const calculateUserFreeTrialDaysLeft = (user) => {
  const daysLeft = moment().diff(moment(user.stripe.trial_end), "days") * -1;
  return daysLeft;
};

export const isStandard = (activeUser) => {
  const currentPlan = get(activeUser, "stripe.plan", "launch_freeTrial");
  return (
    currentPlan === "v2_standard_monthly" ||
    currentPlan === "v2_standard_annually"
  );
};

export const isFreeTrial = (activeUser) => {
  const currentPlan = get(activeUser, "stripe.plan", "launch_freeTrial");
  return currentPlan === "launch_freeTrial" || currentPlan === "free_trial";
};

export const hasActiveSubscription = (user) => {
  if (!user || !isCoach(user)) {
    return false;
  }

  if (!isCoachConverted(user)) {
    return false;
  }

  const billingStatus = user.coachingBilling?.status;
  if (billingStatus && ["unpaid", "canceled"].includes(billingStatus)) {
    return false;
  }

  return true;
};

export const isPullActiveCoach = (user) =>
  !(
    user.permissions?.includes("coach") &&
    (user.coachingBilling?.requestPaymentInfoOnNextLogin ||
      (user.coachingFreeTrial && calculateFreeTrialDaysLeft(user) < 1) ||
      ["unpaid", "canceled"].includes(user.coachingBilling?.status))
  );

export const isPerformActiveCoach = (user) =>
  ["active", "past_due"].includes(user.performBilling?.status);

// Mirrors API isPerformClient (lib/utils/check-user.js).
export const isPerformClient = (user) =>
  user?.performCoach != null && user.performCoach.status !== "cancelled";

// PUSH subscribers and PERFORM clients both follow a coach's published programs.
export const isPushOrPerformClient = (user) =>
  Boolean(user?.subscription) || isPerformClient(user);

export const filterAthletesAttrs = (athlete) =>
  pick(athlete, [
    "_id",
    "name",
    "email",
    "avatar",
    "status",
    "paid",
    "limitations",
    "goals",
    "equipment",
    "notes",
    "joinedAt",
    "groups",
    "questionnaire",
    "annualPlan",
    "isGymMember",
    "isDemo",
    "demoStyle",
    "demoSeeded",
    "acknowledged",
    "requiresAcknowledgement",
    "tasks",
    "legacyTasks",
    "accountability",
    "guides",
  ]);
export const getTestEmail = (email) => {
  const [address, domain] = email.split("@");
  return `${address}+msb@${domain}`;
};

export const getPushTestEmail = (email) => {
  const [address, domain] = email.split("@");
  return `${address}+push@${domain}`;
};

export const isTestAthlete = (athleteEmail, coachEmail) => {
  return athleteEmail === getTestEmail(coachEmail);
};

export const getDemoEmail = (email) => {
  const [address, domain] = email.split("@");
  return `${address}+demo@${domain}`;
};

export const isDemoAthlete = (athleteEmail, coachEmail) => {
  return athleteEmail === getDemoEmail(coachEmail);
};

/** Athlete subdocument on the coach with `isDemo: true`, if any. */
export const findDemoAthleteEntry = (user) =>
  (user?.athletes || []).find((a) => a.isDemo) || null;

export const hasNoAthletes = (user) => {
  const { athletes = [], pendingAthleteInvitations = [] } = user;
  return !athletes.length && !pendingAthleteInvitations.length;
};

/** True when there are no pending invites and no “real” athletes (empty or every entry is demo). */
export const hasNoAthletesOrOnlyDemoAthlete = (user) => {
  const { athletes = [], pendingAthleteInvitations = [] } = user;
  if (pendingAthleteInvitations.length) return false;
  if (!athletes.length) return true;
  return athletes.every((a) => a.isDemo === true);
};
export const formatProfileItem = (goals) => {
  const newGoals = goals.map((item) => {
    if (typeof item.checked === "boolean" && typeof item.creating === "boolean")
      return item;
    let obj = { ...item };
    if (typeof item.checked === "string")
      obj = { ...obj, checked: item.checked === "true" };
    if (typeof item.creating === "string")
      obj = { ...obj, creating: item.creating === "true" };
    return obj;
  });
  return newGoals;
};

/**
 * This function is returning the coach id of the athlete,
 * if the athlete is inside the array, the coach would be the active user, if not,
 * it's going to be the organization coach.
 * @param {*} athletes
 * @param {*} athleteId
 * @param {*} activeUserId
 * @param {*} organizationCoach
 * @returns
 */
export const whoIsAthleteCoach = (
  athletes,
  athleteId,
  activeUserId,
  organizationCoach
) => {
  const isFromActiveUser = athletes.find(
    ({ _id }) => String(_id) === String(athleteId)
  );
  const userId = isFromActiveUser ? activeUserId : organizationCoach;
  return userId;
};

/**
 * Function to know if the coach of the athlete is the same than active user
 * @param {*} activeUserId
 * @param {*} organizationCoachId
 * @returns
 */
export const isActiveUserTheCoach = (activeUserId, organizationCoachId) => {
  return String(activeUserId) === String(organizationCoachId);
};

export const canViewAllOrganizationCoaches = (organizationRoleName) =>
  canViewAllOrganizationClients(organizationRoleName);

/**
 * Function to know if the coach of the athlete is the active user, and return the correct coach
 * @param {*} athletes
 * @param {*} athleteId
 * @param {*} organizationActiveUsers
 * @returns
 */
export const getOrganizationCoach = (
  activeUser,
  athleteId = "",
  organizationActiveUsers = []
) => {
  const athletesOfActiveUser = activeUser.athletes || [];
  const activeUserId = activeUser._id;

  const isFromOtherOrganizationMember = !athletesOfActiveUser.some(
    ({ _id }) => _id === athleteId
  );

  let organizationCoachId = activeUserId;
  if (isFromOtherOrganizationMember) {
    const foundCoach = organizationActiveUsers.find(({ athletes = [] }) =>
      athletes.some(({ _id }) => _id === athleteId)
    );
    organizationCoachId = foundCoach ? foundCoach._id : activeUserId;
  }

  return {
    isFromOtherOrganizationMember,
    organizationCoachId,
  };
};

export const getCoachFromAllAthletes = (
  activeUser,
  athlete = "",
  allAthletesFromOrganization = []
) => {
  const athletesOfActiveUser = activeUser.athletes || [];
  const activeUserId = activeUser._id;

  const isFromOtherOrganizationMember = !athletesOfActiveUser.some(
    ({ _id }) => _id === athlete
  );

  let organizationCoachId = activeUserId;
  if (isFromOtherOrganizationMember) {
    const foundAthlete = allAthletesFromOrganization.find(
      ({ athleteId }) => athleteId === athlete
    );
    organizationCoachId = foundAthlete?.coachId ?? activeUserId;
  }

  return {
    isFromOtherOrganizationMember,
    organizationCoachId,
  };
};

/**
 * Parse de athlete's information
 * @param {*} athletes
 * @returns
 */
export const parseAthletesOrganizationInfoToClientList = (athletes) => {
  return athletes.map((obj) => ({
    groups: obj.athleteGroups,
    joinedAt: obj.athleteJoinedAt,
    name: obj.athleteName,
    paid: obj.athletePaid,
    status: obj.athleteStatus,
    _id: obj.athleteId,
    coachId: obj.coachId,
    avatar: obj.athleteAvatar,
    coachName: obj.coachName,
    email: obj.athleteEmail,
    ...(obj.athleteIsGymMember !== undefined && {
      isGymMember: obj.athleteIsGymMember,
    }),
    ...(obj.athleteAcknowledged !== undefined && {
      acknowledged: obj.athleteAcknowledged,
    }),
    ...((obj.athleteTasks ?? obj.tasks) !== undefined && {
      tasks: obj.athleteTasks ?? obj.tasks,
    }),
    ...(obj.athleteRequiresAcknowledgement !== undefined && {
      requiresAcknowledgement: obj.athleteRequiresAcknowledgement,
    }),
  }));
};

/**
 * Parse de coach's information to show it in the chat list
 * @param {*} activeUser
 * @returns
 */
export const parseCoachInformationToChat = (activeUser) => {
  return [activeUser];
};

/**
 * Parse de athlete's information
 * @param {*} athletes
 * @returns
 */

export const parseAthletesOrganizationInfoToClientListCoachRole = (
  activeUser
) => {
  const athletes = activeUser.athletes || [];
  return athletes.map((obj) => ({
    athleteAvatar: obj.avatar,
    athleteGroups: obj.groups,
    athleteId: obj._id,
    athleteName: obj.name,
    athletePaid: obj.paid,
    athleteStatus: obj.status,
    athleteJoinedAt: obj.joinedAt,
    coachId: activeUser._id,
    coachName: activeUser.name,
  }));
};

/**
 * Parse de athlete's information.
 * @param {*} athletes
 * @returns
 */
export const parseAthletesOrganizationInfoToGroups = (athletes) => {
  return athletes.map((obj) => {
    const parsedObj = {};

    if (obj.athleteGroups !== undefined) parsedObj.groups = obj.athleteGroups;
    if (obj.athleteJoinedAt !== undefined)
      parsedObj.joinedAt = obj.athleteJoinedAt;
    if (obj.athleteName !== undefined) parsedObj.name = obj.athleteName;
    if (obj.athletePaid !== undefined) parsedObj.paid = obj.athletePaid;
    if (obj.athleteStatus !== undefined) parsedObj.status = obj.athleteStatus;
    if (obj.athleteId !== undefined) parsedObj._id = obj.athleteId;
    if (obj.coachId !== undefined) parsedObj.coachId = obj.coachId;
    if (obj.athleteAvatar !== undefined) parsedObj.avatar = obj.athleteAvatar;
    if (obj.coachName !== undefined) parsedObj.coachName = obj.coachName;
    if (obj.athleteQuestionnaire !== undefined)
      parsedObj.questionnaire = obj.athleteQuestionnaire;
    if (obj.athleteNotes !== undefined) parsedObj.notes = obj.athleteNotes;
    if (obj.athleteEquipment !== undefined)
      parsedObj.equipment = obj.athleteEquipment;
    if (obj.athleteGoals !== undefined) parsedObj.goals = obj.athleteGoals;
    if (obj.athleteGuides !== undefined) parsedObj.guides = obj.athleteGuides;
    if (obj.athleteLimitations !== undefined)
      parsedObj.limitations = obj.athleteLimitations;
    if (obj.athleteAccountability !== undefined)
      parsedObj.accountability = obj.athleteAccountability;
    if (obj.athleteTasks !== undefined) parsedObj.tasks = obj.athleteTasks;
    if (obj.athleteEmail !== undefined) parsedObj.email = obj.athleteEmail;
    if (obj.athleteIsGymMember !== undefined)
      parsedObj.isGymMember = obj.athleteIsGymMember;
    if (obj.athleteAcknowledged !== undefined)
      parsedObj.acknowledged = obj.athleteAcknowledged;
    if (obj.athleteRequiresAcknowledgement !== undefined)
      parsedObj.requiresAcknowledgement = obj.athleteRequiresAcknowledgement;

    return parsedObj;
  });
};

// update data of athlete in athlete's array with data from athlete user doc
export const syncAthleteData = (athleteObj, athleteUser) => {
  if (athleteUser) {
    athleteObj.name = athleteUser.name;
    athleteObj.email = athleteUser.email;
    athleteObj.avatar = athleteUser.avatar;
  }
};

const getTimeZoneOffset = (timeZoneName) => {
  const formatter = new Intl.DateTimeFormat([], { timeZoneName });
  const parts = formatter.formatToParts(new Date());
  const timeZone = parts.find((part) => part.type === "timeZoneName").value;
  const numericOffset = timeZone.replace("GMT", "");
  return numericOffset ? String(numericOffset) : "-4";
};

export const getUserTimeZone = () => {
  try {
    return getTimeZoneOffset("shortOffset");
  } catch {
    return getTimeZoneOffset("short");
  }
};
